import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import type { ClientBeneficiariesClient } from '@/api/clients-beneficiaries-client';
import {
  ClientBeneficiariesClientProvider,
  useClientBeneficiariesClient,
} from '@/api/clients-beneficiaries-client-context';
import { ClientsBeneficiariesSection } from '@/routes/admin/clients/clients-beneficiaries-section';

// =============================================================================
// clients-beneficiaries-panel.tsx — Conteneur ROUTÉ des clients bénéficiaires d'une campagne (S33).
// =============================================================================
// Branche la SECTION présentationnelle (clients-beneficiaries-section) sur l'ENDPOINT RÉEL via le client
// HTTP (le stub cède la place à l'impl réelle) : c'est le rattrapage du faux-vert ARCH « ClientsBeneficiaries
// monté nulle part → non atteignable end-to-end ». Le panneau AUTO-PORTE son DI seam (provider local, défaut
// HTTP) ; les tests injectent un double via la prop `client`. L'autorité (gate de diffusion, masquage des
// noms) reste l'API — ce conteneur ne fait qu'orchestrer requête → rendu.

interface ClientsBeneficiariesPanelProps {
  /** Campagne dont on présente les bénéficiaires (grain per-campagne du flag de diffusion). */
  readonly campaignId: string;
  /** DI seam optionnel (test) ; défaut = client HTTP réel via le contexte. */
  readonly client?: ClientBeneficiariesClient | undefined;
}

export function ClientsBeneficiariesPanel({
  campaignId,
  client,
}: ClientsBeneficiariesPanelProps): React.JSX.Element {
  return (
    <ClientBeneficiariesClientProvider client={client}>
      <ClientsBeneficiariesPanelInner campaignId={campaignId} />
    </ClientBeneficiariesClientProvider>
  );
}

function ClientsBeneficiariesPanelInner({
  campaignId,
}: {
  readonly campaignId: string;
}): React.JSX.Element {
  const client = useClientBeneficiariesClient();
  const query = useQuery({
    queryKey: ['clients-beneficiaries', campaignId],
    queryFn: () => client.listBeneficiaries(campaignId),
  });

  if (query.isPending) {
    return (
      <section aria-label="Clients bénéficiaires">
        <p role="status" className="text-muted-foreground">
          Chargement des clients bénéficiaires…
        </p>
      </section>
    );
  }

  if (query.isError) {
    return (
      <section aria-label="Clients bénéficiaires">
        <p role="status" className="text-muted-foreground">
          Les clients bénéficiaires ne sont pas disponibles pour le moment.
        </p>
      </section>
    );
  }

  return <ClientsBeneficiariesSection view={query.data} />;
}
